/** CreateMatterModal — form modal for creating a new matter. */

import { useState, type FormEvent } from "react";
import { Modal, Input, Select, message } from "antd";
import { useCreateMatter, useClients, useUsers } from "../../api/queries";
import { MATTER_TYPE_LABELS } from "../../constants";
import type { MatterType } from "../../types";
import styles from "../../styles/matter.module.css";

const MATTER_TYPE_OPTIONS = Object.entries(MATTER_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

interface CreateMatterForm {
  name: string;
  clientId: string;
  type: MatterType;
  leadLawyerId: string;
  description: string;
}

const INITIAL_FORM: CreateMatterForm = {
  name: "",
  clientId: "",
  type: "litigation" as MatterType,
  leadLawyerId: "",
  description: "",
};

interface CreateMatterModalProps {
  open: boolean;
  onClose: () => void;
  defaultClientId?: string;
}

export default function CreateMatterModal({
  open,
  onClose,
  defaultClientId,
}: CreateMatterModalProps) {
  const [form, setForm] = useState<CreateMatterForm>({
    ...INITIAL_FORM,
    clientId: defaultClientId ?? "",
  });
  const createMatter = useCreateMatter();
  const [messageApi, contextHolder] = message.useMessage();

  // Fetch clients for the client select dropdown
  const { data: clientsData } = useClients({ page: 1, pageSize: 100 });
  const { data: usersData } = useUsers({ pageSize: 100 });

  const clientOptions =
    clientsData?.data?.map((c) => ({
      value: c.id,
      label: c.name,
    })) ?? [];

  const lawyerOptions =
    (usersData?.data ?? []).map((u) => ({
      value: u.id,
      label: u.name,
    }));

  const updateField = <K extends keyof CreateMatterForm>(
    key: K,
    value: CreateMatterForm[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      messageApi.warning("请输入事项名称");
      return;
    }

    if (!form.clientId) {
      messageApi.warning("请选择所属客户");
      return;
    }

    try {
      await createMatter.mutateAsync({
        name: form.name.trim(),
        clientId: form.clientId,
        type: form.type,
        leadLawyerId: form.leadLawyerId || undefined,
        description: form.description.trim() || null,
      });

      messageApi.success("事项创建成功");
      setForm({ ...INITIAL_FORM });
      onClose();
    } catch {
      messageApi.error("创建失败，请稍后重试");
    }
  };

  const handleClose = () => {
    if (!createMatter.isPending) {
      setForm({ ...INITIAL_FORM });
      onClose();
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="新建事项"
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        okText="确认"
        cancelText="取消"
        confirmLoading={createMatter.isPending}
        okButtonProps={{ className: "" }}
        cancelButtonProps={{ className: "" }}
        width={560}
        rootClassName="jerryCreateMatterModal"
        destroyOnHidden
      >
        <form onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            {/* Name — full width */}
            <div className={styles.formGroupFull}>
              <label className={styles.formLabelRequired}>事项名称</label>
              <Input
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="如：张三诉李四合同纠纷"
                maxLength={100}
              />
            </div>

            {/* Client */}
            <div className={styles.formGroup}>
              <label className={styles.formLabelRequired}>所属客户</label>
              <Select
                value={form.clientId || undefined}
                onChange={(v) => updateField("clientId", v ?? "")}
                options={clientOptions}
                placeholder="选择客户"
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                }
              />
            </div>

            {/* Matter Type */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>事项类型</label>
              <Select
                value={form.type}
                onChange={(v) => updateField("type", v)}
                options={MATTER_TYPE_OPTIONS}
              />
            </div>

            {/* Lead Lawyer */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>主办律师</label>
              <Select
                value={form.leadLawyerId || undefined}
                onChange={(v) => updateField("leadLawyerId", v ?? "")}
                options={lawyerOptions}
                placeholder="选择主办律师（可选）"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                }
              />
            </div>

            {/* Description — full width */}
            <div className={styles.formGroupFull}>
              <label className={styles.formLabel}>描述</label>
              <Input.TextArea
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="简要描述事项背景和委托范围"
                rows={3}
                maxLength={500}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
